import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Layers, FileText, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSlideIdx, setPreviewSlideIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/gallery`, { withCredentials: true })
      .then(res => setItems(res.data))
      .catch(() => toast.error("Failed to load gallery"))
      .finally(() => setLoading(false));
  }, []);

  const openPreview = async (item) => {
    setPreviewSlideIdx(0);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/gallery/${item.id}`, { withCredentials: true });
      setPreviewItem(res.data);
    } catch {
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Keyboard nav for the carousel — left/right cycles slides while the
  // modal is open. We only bind the listener while a multi-slide deck is
  // being previewed, so single slides + closed-modal state are no-ops.
  useEffect(() => {
    const total = previewItem?.slide_thumbnails?.length || 0;
    if (!previewItem || previewItem.source_type !== "deck" || total <= 1) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") setPreviewSlideIdx(i => Math.max(0, i - 1));
      else if (e.key === "ArrowRight") setPreviewSlideIdx(i => Math.min(total - 1, i + 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [previewItem]);

  // Download intentionally removed (per product spec): users browse the
  // gallery for inspiration only — they pick a template via the generator
  // flow rather than downloading raw template PPTX files. Click-through
  // on a card opens the preview modal (view-only).

  return (
    <div className="page-container" data-testid="gallery-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Home</span><span>/</span><span className="text-gray-900">Gallery</span>
      </div>
      <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Template Gallery</h1>
      <p className="text-sm text-gray-500 mb-6">Browse curated presentation templates for inspiration</p>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50/50 max-w-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="w-10 h-10 text-gray-300 mb-2" />
            <h3 className="text-sm font-medium text-gray-700">No templates in gallery yet</h3>
            <p className="text-xs text-gray-400 mt-1">Admins can add templates from the admin panel</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(item => (
            <Card key={item.id}
              className="border-gray-200 shadow-none hover:shadow-md transition-all overflow-hidden cursor-pointer group"
              data-testid={`gallery-card-${item.id}`}
              onClick={() => openPreview(item)}>
              {/* Thumbnail */}
              <div className="aspect-video relative flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                <img src={`${API}/thumbnails/${item.source_id}`} alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                  }}
                />
                <div className="hidden flex-col items-center text-gray-400">
                  {item.source_type === "deck" ? <Layers className="w-10 h-10 mb-1" /> : <FileText className="w-10 h-10 mb-1" />}
                </div>
                <div className="absolute top-2 left-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                    item.source_type === "deck" ? "text-indigo-700 bg-indigo-50/90 border border-indigo-200" : "text-gray-500 bg-white/90 border border-gray-200"
                  }`}>
                    {item.source_type === "deck" ? <><Layers className="w-2.5 h-2.5" />Deck</> : <><FileText className="w-2.5 h-2.5" />Slide</>}
                  </span>
                </div>
                {item.num_slides && (
                  <div className="absolute bottom-2 right-2">
                    <span className="text-[10px] font-medium text-gray-600 bg-white/90 rounded-full px-2 py-0.5 border border-gray-200">
                      {item.num_slides} slides
                    </span>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{item.title}</p>
                {item.description && <p className="text-xs text-gray-500 line-clamp-1 mb-2">{item.description}</p>}
                {(item.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{tag}</span>
                    ))}
                    {item.tags.length > 3 && <span className="text-[10px] text-gray-400">+{item.tags.length - 3}</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg">{previewItem?.title}</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : previewItem && (
            <div>
              {/* Slide preview */}
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative mb-4 flex items-center justify-center">
                {previewItem.source_type === "deck" && previewItem.slide_thumbnails?.length > 0 ? (
                  <>
                    <img
                      src={`${API}/thumbnails/${previewItem.slide_thumbnails[previewSlideIdx] || previewItem.source_id}`}
                      alt={`Slide ${previewSlideIdx + 1}`}
                      className="w-full h-full object-contain"
                      data-testid={`gallery-preview-slide-${previewSlideIdx}`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {previewItem.slide_thumbnails.length > 1 && (
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 items-center bg-black/60 rounded-full px-3 py-1.5">
                        <button onClick={(e) => { e.stopPropagation(); setPreviewSlideIdx(Math.max(0, previewSlideIdx - 1)); }}
                          disabled={previewSlideIdx === 0} className="text-white disabled:opacity-30"
                          data-testid="gallery-preview-prev">
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-white font-medium">{previewSlideIdx + 1} / {previewItem.slide_thumbnails.length}</span>
                        <button onClick={(e) => { e.stopPropagation(); setPreviewSlideIdx(Math.min(previewItem.slide_thumbnails.length - 1, previewSlideIdx + 1)); }}
                          disabled={previewSlideIdx >= previewItem.slide_thumbnails.length - 1} className="text-white disabled:opacity-30"
                          data-testid="gallery-preview-next">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <img
                    src={`${API}/thumbnails/${previewItem.source_id}`}
                    alt="Preview"
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              {/* Thumbnail strip — quick slide jumper (multi-slide decks only) */}
              {previewItem.source_type === "deck" && previewItem.slide_thumbnails?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="gallery-preview-strip">
                  {previewItem.slide_thumbnails.map((thumbId, idx) => (
                    <button
                      key={`${thumbId}-${idx}`}
                      onClick={() => setPreviewSlideIdx(idx)}
                      className={`flex-shrink-0 w-20 aspect-video rounded border-2 overflow-hidden transition-all ${
                        idx === previewSlideIdx ? 'border-teal-500 ring-2 ring-teal-200' : 'border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`gallery-preview-strip-${idx}`}
                      aria-label={`Go to slide ${idx + 1}`}
                    >
                      <img
                        src={`${API}/thumbnails/${thumbId}`}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
              {previewItem.description && (
                <p className="text-sm text-gray-600 mb-3">{previewItem.description}</p>
              )}
              {(previewItem.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {previewItem.tags.map(tag => (
                    <span key={tag} className="text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-0.5">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
